import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useCallBuckets, CallBucketRow } from '../../hooks/useCallBuckets';

interface CallActivityPanelProps {
  seniorId: string | undefined;
  seniorName: string;
}

/**
 * F02 — "Activity" panel rendering a 7-day grid of call_buckets.
 *
 * Per brief (page 5–6):
 *   - 2 columns: AM/PM (08:00–14:59) and PM/Evening (15:00–21:59)
 *   - Each cell shows the integer count
 *   - "Learning baseline" badge for first 7 days
 *   - After Day 7: anomalous cells highlighted (color + alert icon)
 *
 * Anomaly per cell (column-wise):
 *   today_count / 7_day_avg <= 0.34  AND  7_day_avg >= 1
 *
 * Caregiver only sees the integers — no phone numbers, no durations, no
 * individual timestamps. This is the entire "view" of senior call activity.
 */
const ANOMALY_RATIO = 0.34;
const ANOMALY_MIN_AVG = 1;
const BASELINE_DAYS = 7;

export const CallActivityPanel: FC<CallActivityPanelProps> = ({ seniorId, seniorName }) => {
  const { t, i18n } = useTranslation();
  const { buckets, loading } = useCallBuckets(seniorId);

  // Last 7 calendar days, oldest first; back-fill missing dates with zeros so
  // gaps show clearly rather than rearranging the grid.
  const grid = useMemo(() => buildLast7Days(buckets), [buckets]);
  const isLearning = useMemo(() => buckets.length < BASELINE_DAYS, [buckets]);

  // Column averages over the 7-day window (excluding cells flagged as anomalous
  // would be circular — brief uses raw rolling avg, so do the same).
  const ampmAvg = avg(grid.map(g => g.ampm));
  const pmEveningAvg = avg(grid.map(g => g.pmevening));

  if (loading) {
    return (
      <section className="bg-white rounded-2xl border border-brand-border/70 p-6 animate-pulse">
        <div className="h-4 w-32 bg-brand-border/40 rounded mb-4" />
        <div className="grid grid-cols-2 gap-2">
          {Array.from({ length: 14 }).map((_, i) => (
            <div key={i} className="h-12 bg-brand-border/20 rounded-lg" />
          ))}
        </div>
      </section>
    );
  }

  return (
    <section className="bg-white rounded-2xl border border-brand-border/70 p-6">
      <header className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="material-symbols-outlined text-brand-primary" style={{ fontSize: '20px' }}>
            timeline
          </span>
          <h3 className="font-display text-base font-semibold text-brand-text">
            {t('activity.title', { name: seniorName.split(' ')[0] })}
          </h3>
          {isLearning && (
            <span
              className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200"
              title={t('activity.learningTitle')}
            >
              {t('activity.learning')}
            </span>
          )}
        </div>
      </header>

      {/* Column headers */}
      <div className="grid grid-cols-[80px_1fr_1fr] gap-2 mb-2 text-xs text-brand-text-muted font-medium">
        <div />
        <div className="text-center">{t('activity.ampm')}</div>
        <div className="text-center">{t('activity.pmevening')}</div>
      </div>

      {/* 7-day grid */}
      <div className="space-y-1.5">
        {grid.map((row) => {
          const ampmAnomalous =
            !isLearning && ampmAvg >= ANOMALY_MIN_AVG && row.ampm / ampmAvg <= ANOMALY_RATIO;
          const pmEveningAnomalous =
            !isLearning && pmEveningAvg >= ANOMALY_MIN_AVG && row.pmevening / pmEveningAvg <= ANOMALY_RATIO;

          return (
            <div key={row.date} className="grid grid-cols-[80px_1fr_1fr] gap-2 items-center">
              <div className="text-xs text-brand-text-muted font-medium">
                {formatDateLabel(row.date, t, i18n.language)}
              </div>
              <Cell value={row.ampm} anomalous={ampmAnomalous} />
              <Cell value={row.pmevening} anomalous={pmEveningAnomalous} />
            </div>
          );
        })}
      </div>

      {!isLearning && (
        <p className="mt-3 text-xs text-brand-text-muted leading-relaxed">
          {t('activity.anomalyHint')}
        </p>
      )}
    </section>
  );
};

interface CellProps {
  value: number;
  anomalous: boolean;
}

const Cell: FC<CellProps> = ({ value, anomalous }) => {
  if (anomalous) {
    return (
      <div className="flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 font-semibold">
        <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>warning</span>
        <span>{value}</span>
      </div>
    );
  }
  return (
    <div className="text-center px-3 py-2 rounded-lg bg-brand-bg-subtle border border-brand-border/60 text-brand-text font-medium">
      {value}
    </div>
  );
};

function avg(xs: number[]): number {
  if (xs.length === 0) return 0;
  return xs.reduce((s, x) => s + x, 0) / xs.length;
}

/**
 * Build a stable 7-day window ending today, back-filling any missing date
 * rows with zeros so the grid never re-flows when a senior misses a window.
 */
function buildLast7Days(rows: CallBucketRow[]): Array<{ date: string; ampm: number; pmevening: number }> {
  const byDate: Record<string, CallBucketRow> = {};
  for (const r of rows) byDate[r.date] = r;

  const out: Array<{ date: string; ampm: number; pmevening: number }> = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    const row = byDate[key];
    out.push({
      date: key,
      ampm: row?.ampm_count ?? 0,
      pmevening: row?.pmevening_count ?? 0,
    });
  }
  return out;
}

function formatDateLabel(yyyyMmDd: string, t: (k: string) => string, language?: string): string {
  // Day abbreviation (Mon/Tue/...) + day number. This used to pass `undefined`,
  // which is the BROWSER locale, not the app's — so the 7-day grid stayed
  // English ("21 Tue") with the rest of the dashboard in French.
  const d = new Date(yyyyMmDd + 'T12:00:00'); // noon to avoid TZ rollover
  const todayKey = new Date().toISOString().slice(0, 10);
  if (yyyyMmDd === todayKey) return t('activity.today');
  const locale = language?.startsWith('fr') ? 'fr-CA' : 'en-CA';
  return d.toLocaleDateString(locale, { weekday: 'short', day: 'numeric' });
}

export default CallActivityPanel;
